import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE } from "../utils/apiBase";

const API = API_BASE;

const RedoKundaliPage = () => {
  const [searchParams] = useSearchParams();
  const prefilledEmail = (searchParams.get('email') || '').trim().toLowerCase();

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState({
    full_name: '',
    gender: '',
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: '',
    email: prefilledEmail,
    whatsapp_number: '',
    language: 'hindi',
    reason: '',
  });

  useEffect(() => {
    document.title = 'Kundali Redo · Karma Kundali';
    window.scrollTo(0, 0);
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.full_name.trim().length < 2) return 'Please enter your full name.';
    if (!form.gender) return 'Please select your gender.';
    if (!form.date_of_birth) return 'Please enter your date of birth.';
    if (!form.time_of_birth) return 'Please enter your time of birth.';
    if (form.place_of_birth.trim().length < 2) return 'Please enter your place of birth.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter the email used at payment.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const r = await fetch(`${API}/api/orders/redo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_info: {
            full_name: form.full_name.trim(),
            gender: form.gender,
            date_of_birth: form.date_of_birth,
            time_of_birth: form.time_of_birth,
            place_of_birth: form.place_of_birth.trim(),
            email: form.email.trim().toLowerCase(),
            whatsapp_number: form.whatsapp_number.trim() || '+910000000000',
            language: form.language,
          },
          lookup_email: form.email.trim().toLowerCase(),
          reason: form.reason.trim() || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Could not submit. Please email karmakundali2@gmail.com');
      setDone(true);
      toast.success('Details receive ho gayi!');
    } catch (e2) {
      setErrorMsg(e2.message || 'Something went wrong.');
      toast.error(e2.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="redo-success-page" className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="max-w-xl w-full bg-white rounded-3xl border-2 border-brand-gold/40 shadow-premium p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-700 mx-auto mb-3" />
          <h1 className="font-playfair font-bold text-3xl text-brand-maroon mb-2">Details receive ho gayi</h1>
          <p className="text-brand-text-soft text-sm mb-4">
            Astro Avinash aapki sahi nayi Kundali tayar karne lag gaye hain. <strong>2 ghante ke andar</strong> aapke email <code className="bg-brand-cream px-1 rounded">{form.email}</code> par PDF aa jayegi.
          </p>
          <p className="text-brand-text-soft text-xs">
            Spam ya Promotions tab bhi check kar lijiye. Koi bhi sawaal ho to <a href="mailto:karmakundali2@gmail.com" className="text-brand-maroon underline">karmakundali2@gmail.com</a> par likh dijiye.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="redo-page" className="min-h-screen bg-brand-cream relative">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-25 pointer-events-none" />
      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] rounded-3xl shadow-premium border border-brand-gold/40 px-6 py-7 mb-6 text-center overflow-hidden relative">
          <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-25 pointer-events-none" />
          <div className="relative">
            <Sparkles className="w-8 h-8 text-brand-gold-light mx-auto mb-2" />
            <p className="text-brand-gold-light/70 text-xs tracking-[0.3em] uppercase mb-1">Kundali Redo · Free</p>
            <h1 className="font-playfair font-bold text-3xl text-brand-gold-light leading-tight">
              Apni sahi details bhar dijiye
            </h1>
            <p className="text-white/75 text-sm mt-2 max-w-md mx-auto">
              Pehle ki payment se hi nayi Kundali ban jayegi — koi extra charge nahi. 2 ghante ke andar email pe aa jayegi.
            </p>
          </div>
        </div>

        {/* Friendly explainer — no email verification, just trust the customer */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <strong>Note:</strong> Aap apni sahi birth details neeche bhar dijiye. Astro Avinash khud aapki nayi Kundali tayar karke <strong>2 ghante ke andar</strong> isi email par bhej denge — bilkul free. Koi extra payment nahi karni.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border-2 border-brand-gold/30 shadow-sm p-5 sm:p-7 space-y-4">
          <Field label="Aapka email *" testid="redo-field-email">
            <input
              data-testid="redo-input-email"
              type="email" required
              value={form.email} onChange={setField('email')}
              placeholder="aapka@example.com"
              className={inputCls}
            />
          </Field>

          <Field label="Pura naam (sahi spelling) *" testid="redo-field-name">
            <input
              data-testid="redo-input-name"
              required minLength={2}
              value={form.full_name} onChange={setField('full_name')}
              className={inputCls}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Gender *" testid="redo-field-gender">
              <select
                data-testid="redo-input-gender"
                required value={form.gender} onChange={setField('gender')}
                className={inputCls}
              >
                <option value="">— Choose —</option>
                <option value="male">Male / पुरुष</option>
                <option value="female">Female / महिला</option>
                <option value="other">Other / अन्य</option>
              </select>
            </Field>
            <Field label="Language *" testid="redo-field-language">
              <select
                data-testid="redo-input-language"
                value={form.language} onChange={setField('language')}
                className={inputCls}
              >
                <option value="hindi">Hindi / हिंदी</option>
                <option value="english">English</option>
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Date of birth *" testid="redo-field-dob">
              <input
                data-testid="redo-input-dob"
                type="date" required
                value={form.date_of_birth} onChange={setField('date_of_birth')}
                className={inputCls}
              />
            </Field>
            <Field label="Time of birth *" testid="redo-field-tob">
              <input
                data-testid="redo-input-tob"
                type="time" required
                value={form.time_of_birth} onChange={setField('time_of_birth')}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Place of birth (city, state) *" testid="redo-field-pob">
            <input
              data-testid="redo-input-pob"
              required minLength={2}
              value={form.place_of_birth} onChange={setField('place_of_birth')}
              placeholder="e.g. Lucknow, Uttar Pradesh"
              className={inputCls}
            />
          </Field>

          <Field label="Pehle wali Kundali me kya galat tha? (optional)" testid="redo-field-reason">
            <textarea
              data-testid="redo-input-reason"
              rows={3} maxLength={500}
              value={form.reason} onChange={setField('reason')}
              placeholder="e.g. Mera naam galat tha / DOB galat thi / Email pe Kundali nahi mili"
              className={inputCls}
            />
          </Field>

          {errorMsg && (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 text-xs text-red-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <button
            type="submit" disabled={submitting}
            data-testid="redo-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark disabled:opacity-60 text-brand-gold-light font-bold px-6 py-4 rounded-full shadow-maroon-soft hover:-translate-y-0.5 transition-all text-base"
          >
            {submitting
              ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>)
              : (<>Submit & Get Corrected Kundali</>)}
          </button>

          <p className="text-center text-xs text-brand-text-soft flex items-center justify-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            Need help? Email <a href="mailto:karmakundali2@gmail.com" className="text-brand-maroon underline">karmakundali2@gmail.com</a>
          </p>
        </form>
      </div>
    </div>
  );
};

const inputCls =
  'w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm text-brand-text-dark';

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold text-brand-text-dark mb-1.5 tracking-wider uppercase">{label}</span>
    {children}
  </label>
);

export default RedoKundaliPage;
